import { Alert, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { useState } from 'react';
import { ApiError } from '../api/errors';

/**
 * The one dialog for "you are writing into a month your accountant has already filed".
 *
 * The office does not stop him. The design decision behind the whole feature is that the person
 * meeting this is the owner with a real piece of paper in his hand — the gas man's bill that came
 * late, the missed punch reported on payday — and refusing him leaves the paper unrecorded, which
 * is a worse book than one with a late entry in it. So the screen tells him what he is about to do,
 * takes a reason, and sends the same thing again.
 *
 * ## Why the server's sentence is shown verbatim
 *
 * `api/errors.ts` says, correctly, that the portal chooses the words and maps on the CODE, because
 * a server message is written to be matched on rather than read aloud. This is the exception, and
 * it is the exception for a reason the rule cannot cover: the sentence names WHICH month the entry
 * landed in and WHEN it was filed, and neither is something the browser can work out. A punch at
 * 01:30 on the first of a month belongs to the night before, and therefore to the previous month's
 * return — that is the server's business-day rule, not a date the form can slice. Composing our own
 * sentence here would mean guessing the month, and guessing it wrong on exactly the entries this
 * warning exists for.
 */
export const FILED_MONTH_CODE = 'month_already_filed';

/** True when a failed save is this warning rather than a genuine refusal. */
export function isFiledMonthWarning(err: unknown): err is ApiError {
  return err instanceof ApiError && err.code === FILED_MONTH_CODE;
}

/** The field name the API takes. One string, so a caller cannot misspell it into silence. */
export const FILED_MONTH_REASON_FIELD = 'filedMonthReason';

export interface WritingIntoAFiledMonthProps {
  /** The warning the server answered with, or null when there is nothing to confirm. */
  warning: ApiError | null;
  onCancel: () => void;
  /** Send the same thing again, carrying the reason. */
  onConfirm: (reason: string) => void;
  saving?: boolean;
}

export function WritingIntoAFiledMonth(props: WritingIntoAFiledMonthProps) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const tooShort = reason.trim().length < 3;

  if (!props.warning) return null;

  return (
    <Modal
      opened
      onClose={props.onCancel}
      title="That month is already filed"
      size="lg"
      // A half-typed reason lost to a stray click means the entry is lost with it.
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Alert color="yellow" variant="light">
          {props.warning.message}
        </Alert>
        <Textarea
          label="Why are you recording it anyway?"
          description="Your accountant reads this. Say what arrived late, or what was missed — a few words is enough."
          placeholder="The bill only reached us this week"
          autosize
          minRows={2}
          maxLength={300}
          value={reason}
          onChange={(e) => {
            setReason(e.currentTarget.value);
            setTouched(true);
          }}
          error={touched && tooShort ? 'Give a short reason so this can be explained later.' : null}
        />
        <Text size="xs" c="dimmed">
          This will be recorded and listed for your accountant as a change made after the month was filed.
          Nothing already filed is altered.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={props.onCancel} disabled={props.saving}>
            Don’t record it
          </Button>
          <Button
            color="yellow"
            loading={props.saving}
            onClick={() => {
              setTouched(true);
              if (!tooShort) props.onConfirm(reason.trim());
            }}
          >
            Record it anyway
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
